import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { Eyebrow, Reveal } from "../components/Reveal";
import { MonthPicker } from "../components/AvailabilityCalendar";
import { trackEvent } from "../analytics/ga4";
import { breadcrumb } from "../components/Seo";

import API from "../api";
const PATH = "/build-your-island-weekend/";

const ADDONS = [
  {
    key: "band",
    name: "Two-Man Band",
    desc: "Two hours of live music in Tom's Bar — their song list, your night.",
    price: 500,
    per: "flat",
  },
  {
    key: "banquet",
    name: "Chinese Banquet",
    desc: "Dim sum and sharing dishes, served long-table on the island.",
    price: 18,
    per: "person",
  },
  {
    key: "bundle",
    name: "Adventure Bundle",
    desc: "Kayaking, stand-up paddleboarding and the beach sauna — one special price.",
    price: 50,
    per: "person",
  },
];
const PRICING = { base: 3300, band: 500, banquet_pp: 18, bundle_pp: 50, ferry_pp: 20 };

const SEO = {
  title: "Build Your Own Island Weekend | Private Island Breaks Westport",
  description:
    "Design a private island weekend in Clew Bay — pick your own dates from live availability, choose sauna, RIB, banquet, band and more, then enquire. Up to 22 guests.",
  path: PATH,
  jsonLd: [breadcrumb(PATH, "Build Your Own Island Weekend")],
};

const labelCls = "block text-xs font-semibold uppercase tracking-[0.18em] text-navy";
const inputCls = "mt-2 w-full rounded-md border border-navy/25 bg-white px-4 py-3 text-base text-navy outline-none transition-all duration-300 focus:border-gold focus:ring-2 focus:ring-gold/25 placeholder:text-navy/40";

export default function IslandWeekendPage() {
  const [step, setStep] = useState(1);
  const [arrival, setArrival] = useState(null);
  const [departure, setDeparture] = useState(null);
  const [guests, setGuests] = useState(10);
  const [addons, setAddons] = useState({ band: false, banquet: false, bundle: false });
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [booked, setBooked] = useState(false);
  const [quoteTotal, setQuoteTotal] = useState(null);

  const toggleAddon = (key) => setAddons((cur) => ({ ...cur, [key]: !cur[key] }));

  const euro = (v) => `€${Number(v).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;
  const lines = [
    { label: "Two nights exclusive island hire — the whole island", amount: PRICING.base },
    { label: `Return ferry — included, every guest`, amount: PRICING.ferry_pp * guests },
    addons.band && { label: "Two-man band — 2 hours of live music", amount: PRICING.band },
    addons.banquet && { label: `Chinese banquet — ${guests} guests`, amount: PRICING.banquet_pp * guests },
    addons.bundle && { label: `Adventure bundle — ${guests} guests`, amount: PRICING.bundle_pp * guests },
  ].filter(Boolean);
  const total = lines.reduce((sum, l) => sum + l.amount, 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!arrival || !departure) {
      toast.error("Choose your arrival and departure dates first");
      setStep(1);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/weekend-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          arrival_date: arrival,
          departure_date: departure,
          guests,
          ...addons,
          name: form.full_name,
          email: form.email,
          phone: form.phone,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (res.status === 409) {
        toast.error(data.detail || "Those dates were just booked — please choose different dates.");
        setStep(1);
        setArrival(null);
        setDeparture(null);
        return;
      }
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Failed to send");
      setQuoteTotal(data.total);
      setBooked(true);
      trackEvent("generate_lead", { value: data.total, guests, occasion: "Build Your Own Island Weekend" });
    } catch {
      toast.error("Your weekend could not be sent — please try again or WhatsApp us.");
    } finally {
      setSending(false);
    }
  };

  const stepHead = ["Dates & guests", "Add to your weekend", "Get your quote"];

  return (
    <PageLayout seo={SEO}>
      <main className="bg-navy pb-24 pt-36 md:pt-44" data-testid="island-weekend-page">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <Eyebrow light>Build Your Own Island Weekend</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-ivory md:text-6xl" data-testid="page-h1">
              Design the weekend, we&rsquo;ll keep the island free
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-ivory/75 md:text-base">
              Pick dates straight from the lodge&rsquo;s live booking diary, add the experiences you want, and send it
              to us as one enquiry. Up to 22 guests, exclusively yours.
            </p>
          </Reveal>

          {booked ? (
            <div className="mt-14 border border-gold/40 bg-navy-2 p-10 text-center" data-testid="weekend-success">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gold text-navy">
                <Check className="h-6 w-6" />
              </span>
              <h2 className="mt-6 font-display text-3xl text-ivory">Your island weekend is on its way — to both of us</h2>
              <p className="mx-auto mt-4 max-w-md text-sm font-light leading-relaxed text-ivory/75">
                {arrival} → {departure} · {guests} guests ·{" "}
                <span className="text-gold-2">{euro(quoteTotal ?? total)}</span> — the full breakdown is in
                your inbox now. We&rsquo;ll be in touch shortly to confirm availability.
              </p>
            </div>
          ) : (
            <>
              <div className="mt-12 flex items-center gap-3" data-testid="weekend-steps">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => n < step && setStep(n)}
                    className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest2 ${step === n ? "text-gold" : n < step ? "text-ivory/70" : "text-ivory/35"}`}
                    data-testid={`weekend-step-${n}`}
                  >
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full border ${step === n ? "border-gold bg-gold text-navy" : n < step ? "border-gold/60" : "border-ivory/25"}`}>{n}</span>
                    {stepHead[n - 1]}
                  </button>
                ))}
              </div>

              <div className="mt-10 border border-ivory/15 bg-navy-2 p-6 md:p-10" data-testid="weekend-panel">
                {step === 1 && (
                  <div data-testid="weekend-dates">
                    <MonthPicker arrival={arrival} departure={departure} onArrival={setArrival} onDeparture={setDeparture} />
                    <div className="mt-8">
                      <span className={labelCls}>How many guests? (up to 22)</span>
                      <div className="mt-3 flex flex-wrap items-center gap-5">
                        <button
                          type="button"
                          onClick={() => setGuests((g) => Math.max(1, g - 1))}
                          data-testid="weekend-guests-minus"
                          className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-xl text-ivory transition-colors hover:border-gold hover:text-gold-2"
                          aria-label="Fewer guests"
                        >
                          −
                        </button>
                        <span className="font-display text-4xl text-ivory" data-testid="weekend-guests-count">
                          {guests}
                        </span>
                        <button
                          type="button"
                          onClick={() => setGuests((g) => Math.min(22, g + 1))}
                          data-testid="weekend-guests-plus"
                          className="flex h-11 w-11 items-center justify-center border border-ivory/25 text-xl text-ivory transition-colors hover:border-gold hover:text-gold-2"
                          aria-label="More guests"
                        >
                          +
                        </button>
                        <span className="text-xs font-light text-ivory/60">
                          Two-night minimum — the whole island, exclusively yours.
                        </span>
                      </div>
                    </div>
                    <div className="mt-8 flex justify-end">
                      <button
                        type="button"
                        disabled={!arrival || !departure}
                        onClick={() => setStep(2)}
                        data-testid="weekend-to-step-2"
                        className="flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Add extras <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div data-testid="weekend-addons">
                    <h2 className="font-display text-2xl text-ivory">{stepHead[1]}</h2>
                    <div className="mt-6 grid gap-4 sm:grid-cols-3">
                      {ADDONS.map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => toggleAddon(a.key)}
                          data-testid={`weekend-addon-${a.key}`}
                          className={`relative flex h-full flex-col border p-5 text-left transition-colors duration-300 ${addons[a.key] ? "border-gold bg-gold/10" : "border-ivory/20 hover:border-gold/50"}`}
                        >
                          {addons[a.key] && (
                            <span className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-navy">
                              <Check className="h-3.5 w-3.5" />
                            </span>
                          )}
                          <span className="font-display text-xl text-ivory">{a.name}</span>
                          <span className="mt-2 flex-1 text-sm font-light leading-relaxed text-ivory/70">{a.desc}</span>
                          <span className="mt-4 text-sm text-gold-2">
                            {a.per === "flat" ? `${euro(a.price)} for the weekend` : `${euro(a.price)} per person — ${euro(a.price * guests)} for ${guests} guests`}
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-5 text-xs font-light text-ivory/60">
                      The return ferry (€{PRICING.ferry_pp} per person) is included as standard — it&rsquo;s added
                      for every guest automatically.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                      <button type="button" onClick={() => setStep(1)} className="flex items-center gap-2 border border-ivory/25 px-6 py-3 text-xs font-semibold uppercase tracking-widest2 text-ivory/80 hover:text-gold-2">
                        <ArrowLeft className="h-4 w-4" /> Dates & guests
                      </button>
                      <button type="button" onClick={() => setStep(3)} data-testid="weekend-to-step-3" className="flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy hover:bg-gold-2">
                        Get my quote <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <form onSubmit={submit} data-testid="weekend-form">
                    <h2 className="font-display text-2xl text-ivory">{stepHead[2]}</h2>
                    <p className="mt-2 text-sm font-light text-ivory/60">
                      {arrival} → {departure} · {guests} guests
                      {addons.band || addons.banquet || addons.bundle
                        ? ` · ${[addons.band && "band", addons.banquet && "banquet", addons.bundle && "adventures"].filter(Boolean).join(", ")}`
                        : ""}
                    </p>
                    <div className="mt-6 grid gap-5 sm:grid-cols-2">
                      <label className="block">
                        <span className={labelCls}>Full name *</span>
                        <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="weekend-name-input" className={inputCls} placeholder="Your name" />
                      </label>
                      <label className="block">
                        <span className={labelCls}>Email *</span>
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="weekend-email-input" className={inputCls} placeholder="you@email.com" />
                      </label>
                      <label className="block">
                        <span className={labelCls}>Phone / WhatsApp *</span>
                        <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="weekend-phone-input" className={inputCls} placeholder="+353 …" />
                      </label>
                      <label className="block sm:col-span-2">
                        <span className={labelCls}>Anything else?</span>
                        <textarea rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="weekend-message-input" className={`${inputCls} resize-none`} placeholder="Occasion, arrival plans, questions…" />
                      </label>
                    </div>
                    <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                      <button type="button" onClick={() => setStep(2)} className="flex items-center gap-2 border border-ivory/25 px-6 py-3 text-xs font-semibold uppercase tracking-widest2 text-ivory/80 hover:text-gold-2">
                        <ArrowLeft className="h-4 w-4" /> Extras
                      </button>
                      <button type="submit" disabled={sending} data-testid="weekend-submit-button" className="flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60">
                        {sending && <Loader2 className="h-4 w-4 animate-spin" />} Email me this quote
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {arrival && departure && (
                <div className="mt-6 border border-gold/40 bg-navy-2 p-6 md:p-8" data-testid="weekend-summary">
                  <h3 className="font-display text-2xl text-ivory">Your weekend so far</h3>
                  <ul className="mt-4 flex flex-col gap-2">
                    {lines.map((l) => (
                      <li key={l.label} className="flex flex-wrap items-baseline justify-between gap-2 text-sm font-light text-ivory/80">
                        <span>{l.label}</span>
                        <span className="text-ivory">{euro(l.amount)}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex items-baseline justify-between border-t border-gold/40 pt-4">
                    <span className="text-[11px] font-semibold uppercase tracking-widest2 text-ivory/60">Total for the weekend</span>
                    <span className="font-display text-3xl text-gold-2" data-testid="weekend-total">
                      {euro(total)}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </PageLayout>
  );
}